import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  startLogin,
  discoverLoginMethod,
  peekLoginHint,
  readRememberedSignIn,
  clearNativeSignIn,
  nativeAutoLoginRecentlyTried,
  markNativeAutoLogin,
} from '../lib/auth.js';
import { safeReturnTo } from '../lib/login-path.js';
import { BrandLogo, ProductMark, PRODUCT_NAME } from '../components/Brand.jsx';
import { MosaicBand, MosaicCorner, MosaicDivider, MosaicPattern } from '../components/Mosaic.jsx';
import NavIcon from '../components/NavIcon.jsx';

// The AWS Marketplace product page is the single source of truth for plans and
// pricing (and for how customer data is handled); this page links to it and
// never repeats a price.
const MARKETPLACE_URL = 'https://marketplace.ipc-global.com/aws/onguard';
const SUPPORT_EMAIL = 'support@ipc-global.com';

// The three value lines of the sign-in story (ENTRAID-347 redesign, mockup 6).
const VALUE_PROPS = [
  { icon: 'security', title: 'A daily security score with what to fix first' },
  { icon: 'activity', title: 'Live alerts that ask the right person “Was this expected?”' },
  { icon: 'reports', title: 'Audit-ready reports, read-only access to your tenant' },
];

const FOOTER_LINKS = [
  { label: 'Privacy', href: 'https://www.ipc-global.com/privacy-policy' },
  { label: 'Terms', href: 'https://www.ipc-global.com/terms-services' },
  { label: 'ipc-global.com', href: 'https://www.ipc-global.com/' },
];

// Every off-site link opens in a new tab without handing over this window.
const EXTERNAL = { target: '_blank', rel: 'noopener noreferrer' };

function SupportLine() {
  return (
    <p className="mt-6 text-center text-xs text-slate-500">
      Trouble signing in?{' '}
      <a className="text-ipc-light hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>
        {SUPPORT_EMAIL}
      </a>
    </p>
  );
}

// The app's own identity at the top of the sign-in card: the A2 mark and the
// product name. The IPC Global logo sits small at the foot of the panel.
function CardHeader() {
  return (
    <div className="mb-8 flex items-center gap-3">
      <ProductMark tone="dark" size={40} />
      <span className="text-lg font-semibold leading-tight tracking-tight text-ipc-primary">
        {PRODUCT_NAME}
      </span>
    </div>
  );
}

// The story: marketing and institutional, clickable. Text on navy is white or
// at least 70% white (>= 4.5:1 on #002F67); Login.test.jsx pins it.
// The mosaic appears as quiet elements only (IPC template rules, see
// brand/onguard/README.md): a tone-on-tone field that dissolves upward from
// the bottom edge, a divider strip on the boundary with the sign-in panel
// (a band when the columns stack), never a block behind content.
function Story() {
  return (
    <aside
      aria-label="About onGuard"
      className="on-dark relative flex overflow-hidden bg-ipc-primary px-6 py-14 text-white sm:px-10 lg:order-first lg:items-center lg:justify-end lg:px-16 lg:py-16 xl:px-24"
    >
      <MosaicPattern />
      <MosaicBand className="absolute inset-x-0 top-0 lg:hidden" />
      <MosaicDivider side="right" className="max-lg:hidden" />
      <div className="relative w-full max-w-xl">
        <div className="flex items-center gap-2.5">
          <ProductMark tone="light" size={32} />
          <span className="text-lg font-semibold tracking-tight text-white">onGuard</span>
          <span className="text-sm text-white/75">by IPC Global</span>
          <span className="ml-auto text-sm font-medium text-[#5BB4F0]">Be on guard.</span>
        </div>

        <h1 className="mt-10 font-display text-[2rem] font-extrabold leading-[1.1] tracking-tight text-white [text-wrap:balance] sm:text-[2.5rem] xl:text-[2.75rem]" data-login-headline="">
          <span className="block">Know what changed in your Entra ID.</span>
          <span className="block">Act on what matters.</span>
        </h1>
        <p className="mt-5 max-w-lg text-lg leading-relaxed text-white/80">
          Daily posture, live alerts on risky changes, and evidence for your audits. Your data stays in your AWS account.
        </p>

        <ul className="mt-10 space-y-4">
          {VALUE_PROPS.map((v) => (
            <li key={v.icon} className="flex gap-3.5">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/10 text-[#5BB4F0]">
                <NavIcon id={v.icon} className="h-5 w-5" />
              </span>
              <span className="self-center font-medium text-white">{v.title}</span>
            </li>
          ))}
        </ul>

        <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
          <a href={MARKETPLACE_URL} {...EXTERNAL} className="btn bg-white text-ipc-primary hover:bg-[#E6F1FB]">
            Get onGuard on AWS Marketplace
          </a>
          <Link to="/security" className="font-medium text-white underline underline-offset-4 hover:text-[#CFE6FA]">
            Trust and security
          </Link>
        </div>

      </div>
    </aside>
  );
}

// Type-your-email-first sign-in. The user enters one email; the platform tells
// us which method serves that domain (their own IdP, or Cognito-native) and we
// redirect there directly. We never show a menu of providers; the domain
// decides, so there is nothing for the user to choose. This is the sign-in
// entry itself, not an interstitial gate.
//
// A returning user (ENTRAID-284) skips even that: the browser remembers the
// account of the last sign-in, so the page goes straight on with the email as
// login_hint (a password account to the native sign-in; an SSO account
// through the same discovery, so the domain still picks the IdP), and shows
// who it is signing in with a "Use a different account" way out. The redirect happens once per
// window; coming back from the IdP shows a Continue / different-account choice
// instead of bouncing. An email another flow already collected for this tab
// (the onboarding claim) always wins over the remembered account.
//
// Layout (ENTRAID-347): the app on the right (white), the story on the left
// (navy). The sign-in comes first in reading order, so it is on top when the
// columns stack below lg.
export default function Login() {
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo') || '/');
  // Prefill with an email this tab already collected (e.g. the onboarding claim).
  const [email, setEmail] = useState(() => peekLoginHint());
  const [remembered, setRemembered] = useState(() => (peekLoginHint() ? null : readRememberedSignIn()));
  // Busy from the first paint when the automatic redirect is about to start.
  const [busy, setBusy] = useState(() => remembered !== null && !nativeAutoLoginRecentlyTried());
  const autoStarted = useRef(false);

  const continueAs = async ({ method: rememberedMethod, email: address }) => {
    setBusy(true);
    try {
      if (rememberedMethod === 'native') {
        await startLogin(returnTo, { method: 'native', email: address });
        return;
      }
      // The stored marker never picks the IdP: the domain does, as on the email form.
      const { method, idpIdentifier } = await discoverLoginMethod(address);
      await startLogin(returnTo, { method, idpIdentifier, email: address });
    } catch {
      // startLogin navigates away on success; if we're still here it failed.
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!remembered || autoStarted.current) return;
    autoStarted.current = true;
    if (nativeAutoLoginRecentlyTried()) return; // came back from the IdP: let the user choose
    if (!markNativeAutoLogin()) {
      setBusy(false); // the once-per-window cap cannot be kept: never redirect on our own
      return;
    }
    continueAs(remembered);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchAccount = () => {
    clearNativeSignIn();
    setRemembered(null);
    setEmail('');
    setBusy(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setBusy(true);
    try {
      const { method, idpIdentifier } = await discoverLoginMethod(value);
      await startLogin(returnTo, { method, idpIdentifier, email: value });
    } catch {
      // startLogin navigates away on success; if we're still here it failed.
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <main className="relative flex flex-col bg-white px-6 py-10 sm:px-10 lg:justify-center lg:px-16 lg:py-8 xl:px-24">
        <div className="mx-auto w-full max-w-[25rem] lg:mx-0">
          {remembered ? (
            <div>
              <CardHeader />
              <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
              <p className="mt-1 text-sm text-slate-600">Signing in as</p>
              <p className="mt-1 break-all text-sm font-medium text-slate-900">{remembered.email}</p>

              <button
                type="button"
                disabled={busy}
                onClick={() => continueAs(remembered)}
                className="btn-primary mt-6 w-full py-2.5 disabled:opacity-60"
              >
                {busy ? 'Taking you to sign-in…' : 'Continue'}
              </button>

              <p className="mt-4 text-center text-sm">
                <button type="button" onClick={switchAccount} className="text-ipc-light hover:underline">
                  Use a different account
                </button>
              </p>

              <SupportLine />
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <CardHeader />
              <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
              <p className="mt-1 text-sm text-ipc-muted">
                Use your work email. We send you to your organization&rsquo;s{' '}
                <span className="whitespace-nowrap">sign-in.</span>
              </p>

              <label htmlFor="email" className="mt-6 block text-sm font-medium text-ipc-ink">
                Work email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1 w-full rounded-md border border-ipc-field px-3 py-2.5 text-sm focus:border-ipc-light focus:outline-none focus:ring-1 focus:ring-ipc-light"
              />

              <button type="submit" disabled={busy} className="btn-primary mt-6 w-full py-2.5 disabled:opacity-60">
                {busy ? 'Taking you to sign-in…' : 'Continue'}
              </button>

              <SupportLine />
            </form>
          )}

          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-ipc-border pt-6">
            <BrandLogo tone="dark" className="h-6 w-auto" />
            <nav aria-label="Legal" className="flex gap-4 text-xs text-slate-500">
              {FOOTER_LINKS.map((l) => (
                <a key={l.label} href={l.href} {...EXTERNAL} className="hover:text-ipc-light hover:underline">
                  {l.label}
                </a>
              ))}
            </nav>
          </div>
        </div>
        <MosaicCorner tone="light" pitch={20} className="max-lg:hidden" />
      </main>

      <Story />
    </div>
  );
}
